import React, { FC, MouseEvent } from "react";

import CheckIcon from "@mui/icons-material/Check";
import { Stack, styled, Tooltip, tooltipClasses, TooltipProps, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IProcessedTag } from "store/types";

import { PsychologyReverse } from "../../../assets/material-custom-icons";
import { RectCSS, Word } from "../../../store/types";

interface RectangleProps {
  className?: string;
  rectangle: any;
  taggedRectangles: Record<string, string>;
  anchorCSS?: RectCSS;
  tags: IProcessedTag[];
  selectedWord: Word | null;
  onClick?: (e: MouseEvent<HTMLDivElement>, id: string, text: string) => void;
  onEnter?: (e: MouseEvent<HTMLDivElement>, id: string, text: string) => void;
  onMouseDown?: (e: MouseEvent<HTMLDivElement>, id: string, text: string) => void;
}

// const wordSelected = (rectId: string, selectedWords: Word[]) => {
//   return selectedWords.some((rect) => rect.rectId === rectId);
// };

const getCss = (rectId: string, tags: IProcessedTag[], selectedWord: Word | null, className?: string) => {
  const css = className ? ["word", className] : ["word"];

  if (selectedWord?.rectId === rectId) {
    css.push("word--selected");
  }

  const tagged = tags.filter((tag) => tag.words.some((rect: Word) => rect.rectId === rectId && !tag.deleted_at));

  if (tagged.length) {
    const isTag = tagged.some((tag) => !tag.is_analysis);
    css.push(isTag ? "word--tagged" : "word--suspect");
    if (tagged.some((tag) => !!tag.is_draft)) {
      css.push("word--draft");
    }
  }
  return css.join(" ");
};

const adjustRectCSS = (anchorCSS: RectCSS, rectCSS: RectCSS): RectCSS => {
  return {
    left: rectCSS.left - anchorCSS.left + 1,
    top: rectCSS.top - anchorCSS.top + 1,
    width: rectCSS.width,
    height: rectCSS.height
  };
};

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "white",
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: "0px 1px 4px 0px rgba(18, 18, 23, 0.12), 0px 1px 2px 0px rgba(18, 18, 23, 0.08)"
  }
}));

const Rectangle: FC<RectangleProps> = (props: RectangleProps) => {
  const correspondingTag = props.tags.find((item) => item.words.some((rect) => rect.rectId === props.rectangle.id));

  const isRecSaved =
    correspondingTag &&
    correspondingTag.is_tagged &&
    !!correspondingTag.measure_data_id &&
    !correspondingTag.deleted_at;
  const isRecIsDraft = correspondingTag && !!correspondingTag.draft_measure_data_id && !correspondingTag.deleted_at;
  const isAIGenerated = correspondingTag?.is_analysis;
  const isAiFromAiData = correspondingTag?.is_ai && !correspondingTag.deleted_at;
  const getBackgroundColorForAi = (tag: typeof correspondingTag) => {
    if (!tag) return "#94949433"; // grey
    if (tag.is_draft) {
      return "#EBAF0199"; // yellow
    }
    if (tag.is_ai && tag.is_tagged && !tag.deleted_at) {
      return "#07A27D80"; // green
    }
    return "#94949433"; // grey
  };

  const backgroundColorForAi = getBackgroundColorForAi(correspondingTag);

  const div = (
    <div
      key={props.rectangle.id}
      className={getCss(props.rectangle.id, props.tags, props.selectedWord, props.className)}
      style={props.anchorCSS ? adjustRectCSS(props.anchorCSS, props.rectangle.css) : props.rectangle.css}
      onClick={(e) => props.onClick?.(e, props.rectangle.id, props.rectangle.text)}
      onMouseEnter={(e) => props.onEnter?.(e, props.rectangle.id, props.rectangle.text)}
      onMouseDown={(e) => props.onMouseDown?.(e, props.rectangle.id, props.rectangle.text)}
    >
      {isRecSaved && (
        <CheckIcon
          sx={{
            background: "#07a27d33",
            fill: "#496682",
            position: "absolute",
            right: "-17px",
            width: "15px",
            height: "100%"
          }}
        />
      )}
      {isRecIsDraft && (
        <CheckIcon
          sx={{
            background: "rgba(252, 198, 81, 0.5);",
            fill: "#ff8000",
            position: "absolute",
            right: "-17px",
            width: "15px",
            height: "100%"
          }}
        />
      )}
      {isAIGenerated && (
        <PsychologyReverse
          sx={{ background: "#07a27d33", position: "absolute", right: "-29px", padding: "0 3px", height: "100%" }}
          fill="black"
        />
      )}
      {isAiFromAiData && (
        <PsychologyReverse
          sx={{
            background: backgroundColorForAi,
            position: "absolute",
            right: "-29px",
            padding: "0 3px",
            height: "100%"
          }}
          fill="black"
        />
      )}
    </div>
  );

  if (props.taggedRectangles[props.rectangle.id]) {
    return (
      <CustomTooltip
        componentsProps={{ popper: { sx: { backgroundColor: "#fffff" } } }}
        title={
          <>
            {correspondingTag?.measure.name ? (
              <Stack direction="row" gap="5px">
                <Typography sx={{ whiteSpace: "nowrap" }}>Parameter : </Typography>
                <MaterialTooltip title={correspondingTag?.measure.name}>
                  <StyledTypography>{correspondingTag?.measure.name}</StyledTypography>
                </MaterialTooltip>
              </Stack>
            ) : (
              <Stack direction="row" gap="5px">
                <Typography sx={{ whiteSpace: "nowrap" }}>Classification : </Typography>
                <MaterialTooltip title={correspondingTag?.classification}>
                  <StyledTypography>{correspondingTag?.classification}</StyledTypography>
                </MaterialTooltip>
              </Stack>
            )}
            <Stack direction="row" gap="5px">
              <Typography sx={{ whiteSpace: "nowrap" }}>Investor : </Typography>
              <MaterialTooltip title={correspondingTag?.investor?.name}>
                <StyledTypography>{correspondingTag?.investor?.name}</StyledTypography>
              </MaterialTooltip>
            </Stack>
            <Stack direction="row" gap="5px">
              <Typography>Currency : </Typography>
              <Typography sx={{ fontWeight: "700" }}>{correspondingTag?.currency}</Typography>
            </Stack>
          </>
        }
      >
        {div}
      </CustomTooltip>
    );
  }

  return div;
};

const StyledTypography = styled(Typography)({
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden"
});

export default React.memo(Rectangle);
